<template>
  <v-container>
    <v-row>
      <v-col cols="12">
        <v-form ref="form">
          <v-container class="py-0">
            <v-toolbar-title>{{$t('keywords.register') }}</v-toolbar-title>
            <v-row>
              <v-col cols="12" md="4">
                <v-text-field
                  :readonly="isUpdating"
                  :rules="alphaNumericRules(databaseDto.tenant)"
                  name="tenant"
                  :label="$t('keywords.id')"
                  :hint="$t('db_register.id_hint')"
                  v-model="databaseDto.tenant"
                />
              </v-col>
              <v-col cols="12" md="4">
                <v-select
                  :items="classnames"
                  :rules="requiredRules(databaseDto.classname)"
                  :label="$t('keywords.db')"
                  v-model="databaseDto.classname"
                ></v-select>
              </v-col>
              <v-col cols="12" md="1">
                <v-switch :label="$t('check.active')" v-model="databaseDto.active" @change="checkSwitch"></v-switch>
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="12" md="4">
                <v-text-field
                  name="urlConnection"
                  :label="$t('db_register.url')"
                  v-model="databaseDto.urlConnection"
                  :rules="databaseURLRules(databaseDto.urlConnection)"
                />
              </v-col>
              <v-col cols="12" md="4">
                <CredentialSelect
                  :rules="requiredRules(databaseDto.credentialName)"
                  name="credentialName"
                  :label="$t('db_register.credential')"
                  v-model="databaseDto.credentialName"
                  type="DATABASE"
                  :required="true"
                />
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="12" class="text-right">
                <v-btn
                  :loading="isTestingConnection"
                  :disabled="isTestingConnection"
                  color="info"
                  @click="testConnection">
                  {{$t('db_register.test')}}
                  <template v-slot:loader>
                    <span class="custom-loader">
                      <v-icon light>mdi-cached</v-icon>
                    </span>
                  </template>
                </v-btn>
                <v-btn
                  :loading="isSaving"
                  :disabled="isSaving"
                  color="success"
                  class="mr-0"
                  @click="saveItem">
                  {{$t('button.save')}}
                  <template v-slot:loader>
                    <span class="custom-loader">
                      <v-icon light>mdi-cached</v-icon>
                    </span>
                  </template>
                </v-btn>
                <v-btn color="blue darken-1" text @click="close">{{$t('button.cancel')}}</v-btn>
              </v-col>
            </v-row>
          </v-container>
        </v-form>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import CadastroDbService from '@/services/CadastroDbService'
import CredentialSelect from '../core/CredentialSelect'
import LocalStorageService from '../../services/LocalStorageService'
import { alphaNumericRules, databaseURLRules, requiredRules } from '../../utils/rules'

export default {
  name: 'CadastroDbForm',

  props: {
    tenantUpdating: String
  },

  components: {
    CredentialSelect
  },

  data: () => ({
    alphaNumericRules,
    requiredRules,
    databaseURLRules,
    databaseDto: {
      tenant: '',
      classname: '',
      urlConnection: '',
      username: '',
      password: '',
      active: true,
      clientPrefix: ''
    },
    showPwd: false,
    classnames: [],
    isUpdating: false,
    isSaving: false,
    isTestingConnection: false
  }),

  mounted () {
    this.loadClassnames()
    this.loadDB()
  },

  methods: {
    loadClassnames () {
      CadastroDbService
        .classnames()
        .then(response => (this.classnames = response.data))
        .catch(error => this.$message.error(`${this.$t('warnings.error.occurred')} : ${error}`))
    },

    loadDB () {
      if (this.tenantUpdating) {
        CadastroDbService
          .findById(this.tenantUpdating)
          .then(response => {
            this.isUpdating = true
            this.databaseDto = response.data
          })
          .catch(error => this.$message.error(`${this.$t('warnings.error.occurred')} : ${error}`))
      }
    },

    testConnection () {
      if (this.validateForm()) {
        this.isTestingConnection = true
        this.databaseDto.clientPrefix = LocalStorageService.getClientPrefix()
        CadastroDbService.testConnection(this.databaseDto)
          .then((response) => {
            if (response.status === 'ERROR') this.$message.error(`${this.$t('db_register.error.connect')} : ${response.errorMessage}`)
            else this.$message.success(this.$t('db_register.success_connect'))
          })
          .catch(error => this.$message.error(`${this.$t('warnings.error.occurred')} : ${error}`))
          .finally(() => { this.isTestingConnection = false })
      }
    },

    saveItem () {
      this.databaseDto.lastModifiedBy = LocalStorageService.getUsername()
      this.databaseDto.clientPrefix = LocalStorageService.getClientPrefix()
      if (this.validateForm()) {
        this.createOrUpdateItem().then(() => this.$emit('editItem', true, null))
      }
    },

    validateForm () {
      if (!this.$refs.form.validate()) {
        this.$message.warning(this.$t('warnings.validation.fill_required_fields'))
        return false
      } else {
        return true
      }
    },

    createOrUpdateItem () {
      this.isSaving = true
      return (this.isUpdating ? this.updateItem() : this.createItem())
        .finally(() => { this.isSaving = false })
    },

    createItem () {
      return CadastroDbService
        .insert(this.databaseDto)
        .catch(error => this.$message.error(`${this.$t('warnings.error.occurred')} : ${error}`))
    },

    updateItem () {
      return CadastroDbService
        .update(this.databaseDto)
        .catch(error => this.$message.error(`${this.$t('warnings.error.occurred')} : ${error}`))
    },

    close () {
      this.$emit('editItem', true, null)
    },

    checkSwitch () {
      if (!this.databaseDto.active) this.$message.warning(this.$t('db_register.warning'))
    }
  }
}
</script>
<style lang="sass" scoped>
.v-btn
  margin: 0 4px

.custom-loader
  animation: loader 1s infinite
  display: flex

@-moz-keyframes loader
  from
    transform: rotate(0)

  to
    transform: rotate(360deg)

@-webkit-keyframes loader
  from
    transform: rotate(0)

  to
    transform: rotate(360deg)

@-o-keyframes loader
  from
    transform: rotate(0)

  to
    transform: rotate(360deg)

@keyframes loader
  from
    transform: rotate(0)

  to
    transform: rotate(360deg)
</style>
